import { useMemo, useCallback, useState } from "react";
import { getDisplayDataType } from "services/reportsV3ServiceHelpers";

const DEFAULT_COLUMN_WIDTH = 150;
const DEFAULT_DATA_TYPE = "text";

const enhanceColumnFromLayers = (specColumn, resultColumnsMap) => {
  const resultCol = resultColumnsMap.get(specColumn.column);
  if (!resultCol) {
    return {
      ...specColumn,
      _canonicalDisplayName: null,
      _dataType: DEFAULT_DATA_TYPE,
      _type: null,
      _subType: null,
      _width: DEFAULT_COLUMN_WIDTH,
      _options: null,
      _inputType: null,
      _inputSubType: null,
      _inputOptions: null,
      _inputDataType: DEFAULT_DATA_TYPE,
      _defaultDecimals: null,
      _inputDefaultDecimals: null,
    };
  }
  const inputDescriptor = resultCol.inputType ?? resultCol.type;
  return {
    column: resultCol.internalName,
    displayName: resultCol.displayName,
    notForExport: resultCol?.notForExport,
    pinned: resultCol.pinned ?? false,
    _canonicalDisplayName: resultCol?.displayName ?? null,
    _dataType:
      getDisplayDataType(resultCol?.type?.type, resultCol?.type?.subType) ??
      DEFAULT_DATA_TYPE,
    _type: resultCol?.type?.type ?? null,
    _subType: resultCol?.type?.subType ?? null,
    _width: DEFAULT_COLUMN_WIDTH,
    _options: resultCol?.type?.enumerations ?? null,
    _inputType: inputDescriptor?.type ?? null,
    _inputSubType: inputDescriptor?.subType ?? null,
    _inputOptions: inputDescriptor?.enumerations ?? null,
    _inputDataType:
      getDisplayDataType(inputDescriptor?.type, inputDescriptor?.subType) ??
      DEFAULT_DATA_TYPE,
    _defaultDecimals: resultCol?.type?.defaultDecimals ?? null,
    _inputDefaultDecimals: inputDescriptor?.defaultDecimals ?? null,
  };
};

export const useEnhancedColumns = () => {
  const [querySpec, setQuerySpecData] = useState([]);
  const [resultColumnsMap, setResultColumnsMapData] = useState(new Map());

  const columns = useMemo(() => {
    if (querySpec.length === 0) return [];
    return querySpec.map((specCol) =>
      enhanceColumnFromLayers(specCol, resultColumnsMap)
    );
  }, [querySpec, resultColumnsMap]);

  const setQuerySpec = useCallback((spec) => {
    setQuerySpecData(spec);
  }, []);

  const setResultColumns = useCallback((columns) => {
    if (!columns) return;
    setResultColumnsMapData(
      new Map(columns.map((col) => [col.internalName, col]))
    );
  }, []);

  const applyColumnRenames = useCallback((overrides) => {
    if (!overrides || Object.keys(overrides).length === 0) return;
    setQuerySpecData((prev) =>
      prev.map((col) => {
        const override = overrides[col.column];
        if (override === undefined) return col;
        if (override === null) return { ...col, displayName: col.column };
        return { ...col, displayName: override };
      })
    );
  }, []);

  const reorderColumns = useCallback((newOrder) => {
    setQuerySpecData((prev) => {
      const specMap = new Map(prev.map((col) => [col.column, col]));

      const reordered = [];
      for (const colName of newOrder) {
        const spec = specMap.get(colName);
        if (spec) {
          reordered.push(spec);
        }
      }

      for (const spec of prev) {
        if (!newOrder.includes(spec.column)) {
          reordered.push(spec);
        }
      }
      return reordered;
    });
  }, []);

  return {
    columns,
    setQuerySpec,
    setResultColumns,
    applyColumnRenames,
    reorderColumns,
  };
};
